import { Controller } from "stimulus";
import slugify from "slugify";

export default class extends Controller {
  static targets = [
    "introduction",
    "form",
    "breakdown",
    "step",
    "stepIcons",
    "previous",
    "submit",
    "error",
  ];
  static values = { step: Number };

  initialize() {
    this.activeStep = 1;
    this.payload = {};
    console.log("SubscriptionFormController initialized");
  }

  handleBegin() {
    if (this.hasIntroductionTarget && this.hasFormTarget) {
      this.introductionTarget.classList.add("subscription-panel-block--hidden");

      this.formTarget.classList.remove("subscription-panel-block--hidden");
    }
  }

  triggerStepChange(e, step) {
    if (step && this.activeStep !== this.stepTargets?.length) {
      this.activeStep = parseInt(step) + 1;
    } else if (e) {
      if (this.activeStep !== this.stepTargets?.length) {
        this.activeStep = parseInt(e.target.dataset.step) + 1;
      }

      this.payload = { ...this.payload, [e.target.name]: e.target.value };
    }

    if (this.payload.customer !== "Myself") {
      this.toggleStep({ step: 5, show: false });
    } else {
      this.toggleStep({ step: 5, show: true });
    }

    if (this.hasStepTarget && this.activeStep <= 6) {
      this.handleActiveStepUI();
    }

    if (this.hasStepIconsTarget) {
      this.handleActiveStepIcons();
    }

    if (this.payload.customer && this.activeStep === 2) {
      this.handleDurations();
    }

    this.handleAvailableOptions();
  }

  toggleStep({ step, show }) {
    const stepTarget = this.stepTargets.find((target) => {
      return target.dataset.stepValue == step;
    });

    if (stepTarget && !show) {
      stepTarget.classList.add("subscription-step--hidden");
      stepTarget.classList.add("subscription-step--visited");
      this.payload.selling_plan = "Monthly";
    } else {
      stepTarget.classList.remove("subscription-step--hidden");
      stepTarget.classList.remove("subscription-step--visited");
    }
  }

  handleAvailableOptions() {
    const data = document.querySelector("#subscription-data")?.innerText;

    if (data) {
      const rawSubscriptionJSON = JSON.parse(data);
      const subscriptionJSON = rawSubscriptionJSON.map((product) => {
        const productRechargeSellingPlanGroups =
          product.selling_plan_groups?.filter(
            (group) => group.app_id === "294517"
          );

        if (productRechargeSellingPlanGroups?.length > 0) {
          product.selling_plan_groups = productRechargeSellingPlanGroups;
        }

        return product;
      });

      let products = [];

      if (this.payload.customer === "Myself" && this.payload.product) {
        products = subscriptionJSON.filter((product) => {
          return product.title === this.payload.product;
        });
      } else if (
        this.payload.customer === "Someone Else" &&
        this.payload.product
      ) {
        products = subscriptionJSON.filter((product) => {
          return product.title.includes(this.payload.product);
        });
      }

      const currentStep = this.stepTargets.find((step) => {
        return step.dataset.stepValue == this.activeStep;
      });

      const variants = products.flatMap((p) => p.variants);

      if (currentStep) {
        currentStep.querySelectorAll("input").forEach((option) => {
          const optionSlug = slugify(option.value, { lower: true });
          const isGrindAvailable = variants.find((variant) => {
            const variantTitleSlug = slugify(variant.title, {
              lower: true,
            });

            if (this.payload.grind) {
              const grindSlug = slugify(this.payload.grind, {
                lower: true,
              });

              return (
                variantTitleSlug.includes(optionSlug) &&
                variantTitleSlug.includes(grindSlug)
              );
            } else {
              return variantTitleSlug.includes(optionSlug);
            }
          });

          if (this.activeStep === 4) {
            if (isGrindAvailable) {
              option.disabled = false;
            } else {
              option.disabled = true;
            }
          }
        });
      }
    }
  }

  handlePreviousStep() {
    if (this.activeStep > 1) {
      const stepState = this.activeStep;

      const hiddenStep = this.stepTargets.find((target) =>
        target.classList.contains("subscription-step--hidden")
      );

      if (
        hiddenStep &&
        this.activeStep == parseInt(hiddenStep.dataset.stepValue) + 1
      ) {
        this.activeStep -= 2;
      } else {
        this.activeStep -= 1;
      }

      this.handleActiveStepIcons();
      this.handleActiveStepUI(stepState);
    }

    if (
      this.formTarget.classList.contains("subscription-panel-block--hidden")
    ) {
      this.activeStep = 6;
      this.breakdownTarget.classList.add("subscription-panel-block--hidden");
      this.formTarget.classList.remove("subscription-panel-block--hidden");
    }
  }

  handleActiveStepIcons() {
    this.stepIconsTarget
      .querySelectorAll(".subscription-form__step-icon")
      .forEach((icon, index) => {
        if (index < this.activeStep) {
          icon.classList.add("tw-bg-black");
          icon.classList.remove("tw-bg-white");
        } else {
          icon.classList.add("tw-bg-white");
          icon.classList.remove("tw-bg-black");
        }
      });
  }

  handleActiveStepUI(previousStep) {
    const hiddenStep = this.stepTargets.find((target) =>
      target.classList.contains("subscription-step--hidden")
    );

    if (hiddenStep && this.activeStep == hiddenStep.dataset.stepValue) {
      this.triggerStepChange(null, hiddenStep.dataset.stepValue);
    }

    if (this.activeStep > 1) {
      this.previousTarget.classList.remove("tw-opacity-0");
      this.previousTarget.classList.remove("tw-pointer-events-none");
    } else {
      this.previousTarget.classList.add("tw-opacity-0");
      this.previousTarget.classList.add("tw-pointer-events-none");
    }

    this.stepTargets.forEach((step) => {
      const value = parseInt(step.dataset.stepValue);
      if (value < this.activeStep) {
        step.classList.add("subscription-step--visited");
      }

      if (value === this.activeStep) {
        step.classList.add("subscription-step--active");

        if (previousStep) {
          step.classList.remove("subscription-step--visited");
        }
      } else {
        step.classList.remove("subscription-step--active");
      }
    });
  }

  async handleSubmit(e) {
    e.preventDefault();

    const data = document.querySelector("#subscription-data")?.innerText;
    const formData = Object.fromEntries(new FormData(e.target));
    // Remove theme editor param
    if (formData.oseid) {
      delete formData.oseid;
    }

    const values = this.stepTargets.filter((step) =>
      step.querySelector("input:checked")
    );

    if (
      values.length == this.stepTargets.length ||
      Object.values(this.payload)?.length == this.stepTargets.length
    ) {
      this.errorTarget.classList.add("tw-hidden");
    } else {
      this.errorTarget.classList.remove("tw-hidden");
      return console.log("Please ensure all fields are filled out");
    }

    if (data) {
      const rawSubscriptionJSON = JSON.parse(data);
      const subscriptionJSON = rawSubscriptionJSON.map((product) => {
        const productRechargeSellingPlanGroups =
          product.selling_plan_groups?.filter(
            (group) => group.app_id === "294517"
          );

        if (productRechargeSellingPlanGroups?.length > 0) {
          product.selling_plan_groups = productRechargeSellingPlanGroups;
        }

        return product;
      });

      const selectedProduct = subscriptionJSON.find((product) => {
        if (formData.customer === "Someone Else") {
          formData.selling_plan = "Monthly";

          return (
            product.title.includes(formData.duration) &&
            product.title.includes(formData.product)
          );
        } else {
          return product.title === formData.product;
        }
      });

      if (!selectedProduct) {
        document.querySelector("#error_modal").showModal();
        return console.log("Product not found");
      }

      const sellingPlanGroup =
        formData.customer !== "Someone Else"
          ? selectedProduct.selling_plan_groups.find((group) => {
              return group.name === formData.selling_plan;
            })
          : {};

      const selectedVariant = selectedProduct.variants.find((variant) => {
        const variantOptions = variant.options;

        if (
          variantOptions.includes(formData.grind) &&
          variantOptions.includes(formData.size)
        ) {
          return true;
        }
      });

      const variantSellingPlan = selectedVariant.selling_plan_allocations.find(
        (plan) => plan.selling_plan_group_id === sellingPlanGroup.id
      );

      const linePayload = {
        id: selectedVariant.id,
        quantity: 1,
      };

      if (variantSellingPlan) {
        linePayload.selling_plan = variantSellingPlan.selling_plan_id;
      }

      this.linePayload = linePayload;

      this.generateBreakdown({
        data: formData,
        price: selectedVariant.price,
        comparePrice: selectedVariant.compare_at_price,
        subscriptionPrice: variantSellingPlan?.price,
      });
    }
  }

  generateBreakdown({ data, price, comparePrice, subscriptionPrice }) {
    this.activeStep += 1;
    if (this.hasFormTarget && this.hasBreakdownTarget) {
      this.introductionTarget.classList.add("subscription-panel-block--hidden");
      this.formTarget.classList.add("subscription-panel-block--hidden");

      this.breakdownTarget.classList.remove("subscription-panel-block--hidden");

      Object.keys(data).forEach((key) => {
        const target = document.querySelector(`[data-breakdown-${key}]`);

        if (target) {
          target.innerText = data[key];
        }
      });

      const formatPrice = (price) => {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: document.querySelector("body").dataset.currencyCode,
        }).format(price / 100);
      };

      document.querySelector("[data-breakdown-price]").innerText = formatPrice(
        subscriptionPrice || price
      );
      document.querySelector("[data-breakdown-compare-price]").innerText =
        comparePrice;
    }
  }

  async processSubscription(e) {
    e.preventDefault();
    const data = new FormData(e.target);

    const discountCode = data.get("discount_code");
    const checkoutUrl = `/checkout${
      discountCode ? `?discount=${discountCode}` : ""
    }`;
    try {
      this.submitTarget.querySelector("span").innerText = "Processing...";
      this.submitTarget.classList.add("button--loading");
      await this.quickCartController.addToCart([this.linePayload]);
      window.location.href = checkoutUrl;
    } catch (err) {
      this.submitTarget.classList.remove("button--loading");
      alert(
        "There was an error processing the subscription. Please try again or get in touch."
      );

      console.log("There was an error processing the subscription", err);
    }
  }

  handleDurations() {
    this.element.querySelectorAll("input[name='duration']").forEach((input) => {
      input.checked = false;

      if (input.dataset.customer === this.payload.customer) {
        input.disabled = false;
        input.parentElement.classList.remove(
          "subscription-form__field--hidden"
        );
      } else {
        input.disabled = true;
        input.parentElement.classList.add("subscription-form__field--hidden");
      }
    });
  }

  get quickCartController() {
    return this.application.controllers.find((controller) => {
      return controller.context.identifier === "quick-cart";
    });
  }
}
